/** #11005 — _playTrack supersede token + stale-safe queue duration backfill (extracted from QueueController). */
import { useMaestroStore } from '../store/maestroStore';
import { diagnosticLog } from '../utils/diagnosticLogger';
import { durationFromTrackRow, normalizeTrackDurationSecs } from '../utils/trackDuration';

let playTrackSeq = 0;

export function resetPlayTrackSupersedeForTests(): void {
  playTrackSeq = 0;
}

/** Each _playTrack entry takes a token; a newer entry invalidates every older in-flight call. */
export function beginPlayTrackToken(): number {
  return ++playTrackSeq;
}

export function isPlayTrackCurrent(token: number): boolean {
  return token === playTrackSeq;
}

/** True (and beacons) when a newer _playTrack began while this one awaited — its dispatch must be dropped. */
export function dropSupersededPlay(token: number, trackId: unknown, stage: string): boolean {
  if (isPlayTrackCurrent(token)) return false;
  diagnosticLog('info', 'playback', 'play_dispatch_superseded', {
    trackId: trackId != null ? String(trackId) : null,
    stage,
  });
  return true;
}

export function resolveQueueTrackDuration(
  track: any,
  streamMeta?: { duration?: number } | null,
): number | undefined {
  const fromTrack = durationFromTrackRow(track);
  if (fromTrack != null) return fromTrack;
  return normalizeTrackDurationSecs(streamMeta?.duration);
}

export function applyResolvedTrackDuration(track: any, durationSecs: number | undefined): any {
  if (!track || durationSecs == null) return track;
  if (durationFromTrackRow(track) != null) return track;
  return { ...track, duration: durationSecs };
}

/**
 * Backfill a missing duration on the queue row for `trackId`. Reads the LIVE store (never a
 * pre-await snapshot) and only rewrites the row that still holds this track, keeping the
 * live queue index — a stale snapshot would reset the queue to an older position/contents.
 */
export function backfillQueueRowDuration(trackId: unknown, durationSecs: number): void {
  if (trackId == null) return;
  const { queue, queueIndex, setQueue } = useMaestroStore.getState();
  const qItem = queue[queueIndex];
  if (!qItem?.track || String(qItem.track.id) !== String(trackId)) return;
  if (durationFromTrackRow(qItem.track) != null) return;
  const nextQueue = [...queue];
  nextQueue[queueIndex] = { ...qItem, track: applyResolvedTrackDuration(qItem.track, durationSecs) };
  setQueue(nextQueue, queueIndex);
}
